import React from 'react';
import {AlertCircle, ChevronRight} from 'lucide-react';

interface Recommendation {
    Id: number;
    name: string;
    enabled: boolean;
    content: string;
    biomarkers?: Array<{
        test_type: string;
        rule_type: string;
        min_value?: number | null;
        max_value?: number | null;
        threshold_value?: number | null;
        rule_expression?: string | null;
    }>;
}

interface RecommendationsProps {
    recommendations: Recommendation[];
    variant?: 'compact' | 'full';
    maxVisible?: number;
    className?: string;
    onViewAll?: () => void;
}

const Recommendations: React.FC<RecommendationsProps> = ({
    recommendations,
    variant = 'full',
    maxVisible = 3,
    className = '',
    onViewAll,
}) => {
    if (!recommendations || recommendations.length === 0) {
        return null;
    }

    const visibleRecommendations = variant === 'compact' ? recommendations.slice(0, maxVisible) : recommendations;

    const hasMore = recommendations.length > maxVisible && variant === 'compact';

    if (variant === 'compact') {
        return (
            <div className={`${className}`}>
                <div className="flex flex-wrap gap-1">
                    {visibleRecommendations.map((rec) => (
                        <span
                            key={rec.Id}
                            className="inline-flex items-center text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-md border border-amber-200"
                            title={rec.content}>
                            <AlertCircle className="h-3 w-3 mr-1" />
                            {rec.name}
                        </span>
                    ))}
                    {hasMore && (
                        <button
                            onClick={onViewAll}
                            className="inline-flex items-center text-xs text-amber-700 hover:text-amber-900 px-2 py-1 rounded-md hover:bg-amber-50 transition-colors">
                            +{recommendations.length - maxVisible} more
                            <ChevronRight className="h-3 w-3 ml-1" />
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div
            className={`bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-4 ${className}`}>
            <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center">
                        <AlertCircle className="h-5 w-5 text-amber-600" />
                    </div>
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-semibold text-amber-900 mb-2">
                        {recommendations.length === 1 ? 'Health Recommendation' : 'Health Recommendations'}
                    </h3>
                    <div className="space-y-3">
                        {recommendations.map((rec) => (
                            <div key={rec.Id} className="bg-white rounded-md p-3 border border-amber-100">
                                <h4 className="font-medium text-amber-900 mb-1">{rec.name}</h4>
                                <p className="text-sm text-amber-800 leading-relaxed">{rec.content}</p>
                                {rec.biomarkers && rec.biomarkers.length > 0 && (
                                    <div className="mt-2 text-xs text-amber-600">
                                        Based on: {rec.biomarkers.map((b) => b.test_type).join(', ')}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                    <div className="mt-3 text-xs text-amber-700">
                        💡 These recommendations are based on your recent lab results. Consult your healthcare provider
                        for personalized advice.
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Recommendations;
